import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

/**
 * Ширина столбцов таблицы, которую пользователь тянет за край шапки.
 *
 * Пока ничего не трогали — таблица в обычной (auto) раскладке, ширины не
 * хранятся. Первое касание «замораживает» текущие ширины всех видимых
 * столбцов (меряем <th data-col-key>), и дальше таблица живёт в
 * table-layout: fixed с явными ширинами. Двойной щелчок по ручке
 * возвращает исходную раскладку целиком.
 *
 * Ширины хранятся в localStorage по ключу таблицы: это удобство одного
 * зрителя, при ошибке доступа просто не сохраняем.
 *
 * На узком экране (NARROW_QUERY) ширины не применяются: там таблицы
 * прокручиваются вбок, превращаются в карточки или прячут столбцы, и
 * заданные на компьютере ширины сломали бы раскладку. Ручка там скрыта.
 */
export const MIN_COLUMN_WIDTH = 60;
export const MAX_COLUMN_WIDTH = 1200;
export const DEFAULT_COLUMN_WIDTH = 150;
export const NARROW_QUERY = '(max-width: 767px)';

const subscribeNarrow = (onChange) => {
  if (typeof window.matchMedia !== 'function') return () => {};
  const query = window.matchMedia(NARROW_QUERY);
  query.addEventListener?.('change', onChange);
  return () => query.removeEventListener?.('change', onChange);
};
const isNarrowScreen = () =>
  typeof window.matchMedia === 'function' && window.matchMedia(NARROW_QUERY).matches;

export const clampWidth = (value) =>
  Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(value)));

/** Оставляет только осмысленные ширины: конечные числа, приведённые к границам. */
export const sanitizeWidths = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const result = {};
  Object.entries(raw).forEach(([key, value]) => {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      result[key] = clampWidth(value);
    }
  });
  return result;
};

const readWidths = (storageKey) => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw ? sanitizeWidths(JSON.parse(raw)) : {};
  } catch {
    return {};
  }
};

const writeWidths = (storageKey, widths) => {
  try {
    if (Object.keys(widths).length === 0) window.localStorage.removeItem(storageKey);
    else window.localStorage.setItem(storageKey, JSON.stringify(widths));
  } catch {
    // Хранилище недоступно — ширины живут до перезагрузки.
  }
};

/** Текущие ширины всех столбцов строки шапки, в которой стоит ручка. */
const measureRow = (handle) => {
  const row = handle?.closest?.('tr');
  const measured = {};
  if (!row) return measured;
  row.querySelectorAll('th[data-col-key]').forEach((cell) => {
    const width = cell.getBoundingClientRect().width;
    // Нулевая ширина бывает у ещё не разложенной таблицы — берём запасную.
    measured[cell.dataset.colKey] = clampWidth(width > 0 ? width : DEFAULT_COLUMN_WIDTH);
  });
  return measured;
};

export const useResizableColumns = (storageKey) => {
  const [widths, setWidths] = useState(() => readWidths(storageKey));
  const stopDragRef = useRef(null);

  useEffect(() => { writeWidths(storageKey, widths); }, [storageKey, widths]);
  useEffect(() => () => stopDragRef.current?.(), []);

  const narrow = useSyncExternalStore(subscribeNarrow, isNarrowScreen, () => false);
  const isResized = !narrow && Object.keys(widths).length > 0;

  const columnWidth = useCallback(
    (key) => widths[key] ?? DEFAULT_COLUMN_WIDTH,
    [widths],
  );

  /** Замораживает раскладку и возвращает исходную ширину столбца. */
  const freeze = useCallback((key, handle) => {
    const measured = measureRow(handle);
    const merged = { ...measured, ...widths };
    setWidths(merged);
    return merged[key] ?? DEFAULT_COLUMN_WIDTH;
  }, [widths]);

  const setColumnWidth = useCallback((key, value) => {
    const next = clampWidth(value);
    setWidths((prev) => (prev[key] === next ? prev : { ...prev, [key]: next }));
  }, []);

  const startResize = useCallback((key, event) => {
    if (event.button !== undefined && event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    stopDragRef.current?.();

    const startX = event.clientX;
    const startWidth = freeze(key, event.currentTarget);

    const onMove = (moveEvent) => setColumnWidth(key, startWidth + moveEvent.clientX - startX);
    const stop = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      document.body.classList.remove('col-resizing');
      stopDragRef.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    document.body.classList.add('col-resizing');
    stopDragRef.current = stop;
  }, [freeze, setColumnWidth]);

  const resetWidths = useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setWidths({});
  }, []);

  /** Ширина таблицы в fixed-раскладке: сумма видимых столбцов. */
  const tableWidth = useCallback(
    (visibleKeys) => visibleKeys.reduce((sum, key) => sum + columnWidth(key), 0),
    [columnWidth],
  );

  /** class и style для <table>: в fixed-раскладке — общий класс и сумма ширин. */
  const tableProps = useCallback((visibleKeys, className = '') => (
    isResized
      ? { className: `${className} col-resized-table`.trim(), style: { width: tableWidth(visibleKeys) } }
      : { className }
  ), [isResized, tableWidth]);

  return {
    widths, isResized, columnWidth, tableWidth, tableProps, startResize, resetWidths,
  };
};

export default useResizableColumns;
